import api from "@/api";
import { usePage } from "@inertiajs/vue3";
import { getMobilePlatform, canUseFreePlay, markFreePlay } from "@/utils/mobilePlatform";

// Signed in, or possibly so while the user is still loading. Guests are answered without a request.
async function isSignedIn({ rootState, dispatch }) {
    if (rootState.user.isLoggedIn) return true;
    if (!window.localStorage.getItem("_token") && !usePage().props.loggedIn) return false;
    try {
        await dispatch("user/getUser", null, { root: true });
    } catch (error) {
        return false;
    }
    return rootState.user.isLoggedIn;
}

const CurrentModule = {
    state: () => ({
        song: {},
        isPlaying: false,
        toggleTick: 0, // bumped when UI outside the player asks it to play/pause
        playedSongs: [], // Track played songs history
        appDownloadPlatform: null,
        appDownloadTitle: null,
        loginPromptTitle: null,
    }),
    mutations: {
        REQUEST_TOGGLE(state) {
            state.toggleTick++;
        },
        SET_PLAYING(state, playing) {
            state.isPlaying = playing;
        },
        UPDATE_SONG(state, payload) {
            state.song = payload;
        },
        SHOW_APP_DOWNLOAD(state, { platform, title }) {
            state.appDownloadPlatform = platform;
            state.appDownloadTitle = title;
        },
        SHOW_LOGIN_PROMPT(state, title) {
            state.loginPromptTitle = title;
        },
        HIDE_LOGIN_PROMPT(state) {
            state.loginPromptTitle = null;
        },
        HIDE_APP_DOWNLOAD(state) {
            state.appDownloadPlatform = null;
            state.appDownloadTitle = null;
        },
        ADD_TO_PLAYED_SONGS(state, payload) {
            // Add song to played history, avoid duplicates at the front
            const existingIndex = state.playedSongs.findIndex(
                (song) => song.id === payload.id,
            );
            if (existingIndex !== -1) {
                // Remove existing entry
                state.playedSongs.splice(existingIndex, 1);
            }
            // Add to the beginning
            state.playedSongs.unshift({
                ...payload,
                playedAt: new Date().toISOString(),
            });
            // Keep only last 50 played songs
            if (state.playedSongs.length > 50) {
                state.playedSongs = state.playedSongs.slice(0, 50);
            }

            // Save to localStorage
            try {
                if (typeof window !== "undefined") {
                    window.localStorage.setItem(
                        "nomad_played_songs",
                        JSON.stringify(state.playedSongs),
                    );
                }
            } catch (error) {
                console.error("Error saving to localStorage:", error);
            }
        },
        LOAD_PLAYED_SONGS(state) {
            // Load from localStorage on app init
            try {
                if (typeof window === "undefined") {
                    return;
                }

                const stored =
                    window.localStorage.getItem("nomad_played_songs");
                if (stored) {
                    state.playedSongs = JSON.parse(stored);
                }
            } catch (error) {
                console.error("Error loading from localStorage:", error);
                state.playedSongs = [];
            }
        },
        CLEAR_PLAYED_SONGS(state) {
            state.playedSongs = [];
            try {
                if (typeof window !== "undefined") {
                    window.localStorage.removeItem("nomad_played_songs");
                }
            } catch (error) {
                console.error("Error clearing localStorage:", error);
            }
        },
    },
    actions: {
        async prepareCurrent({ commit, dispatch, rootState }, payload) {
            const title =
                payload.name ||
                payload.title ||
                (payload.type === "EPISODE" ? "this episode" : "this song");

            // Listening on the website needs an account (the app's APIs are separate)
            if (!(await isSignedIn({ rootState, dispatch }))) {
                commit("SHOW_LOGIN_PROMPT", title);
                return;
            }
            commit("HIDE_LOGIN_PROMPT");

            const mobilePlatform = getMobilePlatform();

            // Phones: today's first song plays here, every other one opens the app prompt
            if (mobilePlatform && !canUseFreePlay(payload.id)) {
                commit("SHOW_APP_DOWNLOAD", {
                    platform: mobilePlatform,
                    title,
                });
                return;
            }

            commit("HIDE_APP_DOWNLOAD");
            // keepOrder: replaying from history leaves the list as is, so prev/next can walk through it
            const { keepOrder, ...request } = payload;
            const current = await api.CurrentApi.fetchSong(request);
            let { mp3, sources } = current.data.data;

            if (payload.stream) {
                current.data.data.stream = payload.stream;
            }

            if (mp3) {
                if (mobilePlatform) markFreePlay(payload.id);
                commit("UPDATE_SONG", current.data.data);
                // Add to played songs history
                if (!keepOrder) commit("ADD_TO_PLAYED_SONGS", current.data.data);
            } else {
                dispatch(
                    "notification/showNotification",
                    { message: "content not available", type: "info" },
                    { root: true },
                );
            }
        },
        loadPlayedSongs({ commit }) {
            commit("LOAD_PLAYED_SONGS");
        },
        clearPlayedSongs({ commit }) {
            commit("CLEAR_PLAYED_SONGS");
        },
    },
    getters: {
        currentSong(state) {
            return state.song;
        },
        isPlaying(state) {
            return state.isPlaying;
        },
        playedSongs(state) {
            return state.playedSongs;
        },
        recentlyPlayed(state) {
            return state.playedSongs.slice(0, 10); // Get last 10 played songs
        },
    },
    namespaced: true,
};

export default CurrentModule;
